import Image from 'next/image';
import { useRouter } from 'next/router';
import React from 'react';

import config from 'configs/app';
import { Link } from 'toolkit/chakra/link';
import { normalizeSearch } from 'ui/home/saba/format';
import Icon from 'ui/home/saba/Icon';
import styles from 'ui/home/saba/SabaHome.module.css';

import layout from './SabaPages.module.css';

export function SabaSearch({ compact = false }: { compact?: boolean }) {
  const router = useRouter();
  const [ search, setSearch ] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);
  const handleSearchChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.target.value), []);
  const handleSearchSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const q = normalizeSearch(search);
      if (q)
        void router.push({
          pathname: '/search-results',
          query: { q, redirect: 'true' },
        });
    },
    [ router, search ],
  );
  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (event.key === 'Escape') {
        inputRef.current?.blur();
      }
      if (
        event.key === '/' &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        !target.closest('input, textarea, select, [contenteditable="true"]')
      ) {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <form className={ `${ styles.search } ${ compact ? layout.compactSearch : '' }` } role="search" onSubmit={ handleSearchSubmit }>
      <label htmlFor="saba-search" className={ styles.srOnly }>
        جست‌وجوی آدرس، تراکنش، بلاک، توکن یا قرارداد
      </label>
      <input
        id="saba-search"
        ref={ inputRef }
        value={ search }
        onChange={ handleSearchChange }
        placeholder="جست‌وجوی آدرس، تراکنش، بلاک، توکن یا قرارداد…"
        autoComplete="off"
        spellCheck={ false }
        dir="auto"
      />
      <kbd>/</kbd>
      <button type="submit" aria-label="جست‌وجو" disabled={ !search.trim() }>
        <Icon name="search" size={ 24 }/>
      </button>
    </form>
  );
}

export default function SabaLayout({ children, home = false }: { children: React.ReactNode; home?: boolean }) {
  const [ menuOpen, setMenuOpen ] = React.useState(false);
  const coin = config.chain.currency.symbol || 'SABA';
  const toggleMenu = React.useCallback(() => setMenuOpen((value) => !value), []);
  React.useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, []);
  return (
    <div className={ `${ styles.home } ${ home ? '' : layout.listPage }` } dir="rtl" lang="fa" data-saba-no-translate>
      <Link className={ styles.skipLink } href="#explorer-content">
        رفتن به محتوای اصلی
      </Link>
      <div className={ home ? styles.hero : undefined }>
        <header className={ styles.header }>
          <Link href="/" className={ styles.brand } aria-label="صفحه اصلی کاوشگر صبا">
            <Image src="/assets/favicon/sabanaft.svg" width="36" height="36" alt=""/>
            <span>
              صبانفت<small>کاوشگر زنجیره صبا</small>
            </span>
          </Link>
          <nav id="saba-navigation" aria-label="ناوبری اصلی" className={ `${ styles.nav } ${ menuOpen ? styles.navOpen : '' }` }>
            <details className={ styles.blockchainMenu }>
              <summary>
                بلاک‌چین <Icon name="chevron" size={ 15 }/>
              </summary>
              <div>
                <Link href="/blocks">بلاک‌ها</Link>
                <Link href="/txs">تراکنش‌ها</Link>
                <Link href="/accounts">حساب‌ها</Link>
                <Link href="/verified-contracts">قراردادهای هوشمند</Link>
                <Link href="/projects">پروژه‌ها</Link>
              </div>
            </details>
            <Link href="/projects">پروژه‌ها</Link>
            <Link href="/verified-contracts">قراردادها</Link>
            <Link href="/txs">تراکنش‌ها</Link>
            <Link href="/accounts">حساب‌ها</Link>
            <Link href="/validators">ولیدیتورها</Link>
            <Link href="/#network-statistics">آمار شبکه</Link>
          </nav>
          <div className={ styles.headerActions }>
            <span className={ styles.networkBadge }>
              <Icon name="network" size={ 15 }/>
              { config.chain.isTestnet ? 'شبکه آزمایشی' : 'شبکه اصلی' }
            </span>
            <button
              type="button"
              className={ styles.mobileMenu }
              aria-label={ menuOpen ? 'بستن منو' : 'باز کردن منو' }
              aria-expanded={ menuOpen }
              aria-controls="saba-navigation"
              onClick={ toggleMenu }
            >
              <Icon name={ menuOpen ? 'close' : 'menu' }/>
            </button>
          </div>
        </header>
        { home && (
          <div className={ styles.heroContent }>
            <h1>کاوش در بلاک‌چین صبا</h1>
            <SabaSearch/>
            <div className={ styles.searchHint }>
              <span>شفافیت در هر بلاک</span>
              <span className={ styles.hintDivider }/>
              <Link href="/blocks">
                مشاهده بلاک‌ها <Icon name="arrow" size={ 13 }/>
              </Link>
            </div>
          </div>
        ) }
      </div>
      { children }
      <footer className={ styles.footer }>
        <div className={ styles.footerTop }>
          <Link className={ styles.brand } href="/">
            <Image src="/assets/favicon/sabanaft.svg" width="30" height="30" alt=""/>
            <span>
              صبانفت<small>کاوشگر زنجیره صبا</small>
            </span>
          </Link>
          <nav aria-label="پیوندهای پایین صفحه">
            <Link href="/blocks">بلاک‌ها</Link>
            <Link href="/txs">تراکنش‌ها</Link>
            <Link href="/projects">پروژه‌ها</Link>
            <Link href="/tokens">توکن‌ها</Link>
            <Link href="/verified-contracts">قراردادها</Link>
            <Link href="/validators">ولیدیتورها</Link>
          </nav>
          <Link className={ styles.backTop } href="#saba-search">
            بازگشت به بالا ↑
          </Link>
        </div>
        <div className={ styles.footerBottom }>
          <span>© شرکت صبانفت؛ همه حقوق محفوظ است.</span>
          <span>
            کاوشگر رسمی زنجیره صبا <span className={ styles.footerDot }>•</span> <bdi>{ coin }</bdi>
          </span>
        </div>
      </footer>
    </div>
  );
}
